import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';

import SuperadminConfig from '../../../config/Superadmin';
import { URL_SEGMENTS, FULL_ROUTES } from '../../../config/Routesconfig';

import Resellers         from '../pages/Resellers.js';
import Clients           from '../pages/Clients.js';
import EnterpriseClients from '../pages/EnterpriseClients.js';
import Admins            from '../pages/Admins.js';
import Account           from '../pages/Account.js';
import Notify            from '../pages/Notify.js';
import Reports           from '../pages/Reports.js';
import ThemeContext       from '../../ThemeContext';

// ───────────────
// Readable route alias — full name instead of SA
// ───────────────
const superadminRoutes = FULL_ROUTES.superadminRoutes;

// ───────────────
// Config shortcuts
// ───────────────
const navConfig     = SuperadminConfig.nav;
const featureFlags  = SuperadminConfig.features;
const clientsConfig = SuperadminConfig.clients;
const reportsConfig = SuperadminConfig.reports;

// ───────────────
// Tab list — driven by featureFlags.tabs
// ───────────────
const ALL_TABS    = ['resellers', 'clients', 'reports', 'notify', 'admins', 'account'];
const visibleTabs = ALL_TABS.filter(tabKey => featureFlags.tabs[tabKey]);

// ───────────────
// Error boundary
// ───────────────
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      const theme = this.context?.theme || {
        colors: { errorBorder: '#f5c2c7', errorText: '#842029', errorBg: '#f8d7da' },
        typography: { fontFamily: 'Arial, sans-serif', bodyFontSize: 14 },
      };
      return (
        <div style={{
          padding: '0.75rem 1.25rem', marginBottom: '1rem',
          border: `1px solid ${theme.colors.errorBorder}`, borderRadius: '0.25rem',
          color: theme.colors.errorText, backgroundColor: theme.colors.errorBg,
          textAlign: 'center', width: '100%', maxWidth: 'min(90vw, 1100px)',
          boxSizing: 'border-box',
          fontFamily: theme.typography?.fontFamily || 'Arial, sans-serif',
          fontSize: `${theme.typography?.bodyFontSize || 14}px`,
        }}>
          {SuperadminConfig.ui.somethingWentWrong}:{' '}
          {this.state.error?.message || SuperadminConfig.ui.unknownError}
        </div>
      );
    }
    return this.props.children;
  }
}
ErrorBoundary.contextType = ThemeContext;

// ───────────────
// Component
// ───────────────
const SuperadminDashboard = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate  = useNavigate();
  const location  = useLocation();

  const [activeTab,    setActiveTab]    = useState('resellers');
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [users,        setUsers]        = useState([]);
  const [error,        setError]        = useState('');
  const [currentUser]                   = useState(JSON.parse(localStorage.getItem('user')) || {});

  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);
  const [isClientsHovered,      setIsClientsHovered]      = useState(false);
  const [isReportsHovered,      setIsReportsHovered]      = useState(false);
  const [isClientsPinned,       setIsClientsPinned]       = useState(false);
  const [isReportsPinned,       setIsReportsPinned]       = useState(false);

  const clientsDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);

  const fontFamily   = theme.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  // ── Detect active tab from URL 
  useEffect(() => {
    const currentPath  = location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);
    const lastSegment  = pathSegments[pathSegments.length - 1];
    const searchParams = new URLSearchParams(location.search);
    const reportSubKey = searchParams.get('sub');

    if (
      currentPath.includes(`/${URL_SEGMENTS.enterpriseClients}`) ||
      currentPath.includes(`/${URL_SEGMENTS.editEnterpriseClient}/`) ||
      currentPath.includes(`/${URL_SEGMENTS.addEnterpriseClient}`)
    ) {
      setActiveTab('clients'); setActiveSubTab('enterprise');

    } else if (
      currentPath.includes(`/${URL_SEGMENTS.clients}`) ||
      currentPath.includes(`/${URL_SEGMENTS.editClient}/`) ||
      currentPath.includes(`/${URL_SEGMENTS.addClient}`)
    ) {
      setActiveTab('clients'); setActiveSubTab('core');

    } else if (currentPath.includes(`/${URL_SEGMENTS.reports}`)) {
      setActiveTab('reports');
      if      (reportSubKey === 'user' || reportSubKey === 'commission' || reportSubKey === 'usage') setActiveSubTab(reportSubKey);
      else if (currentPath.includes(URL_SEGMENTS.userReport))       setActiveSubTab('user');
      else if (currentPath.includes(URL_SEGMENTS.commissionReport)) setActiveSubTab('commission');
      else if (currentPath.includes(URL_SEGMENTS.usageReport))      setActiveSubTab('usage');
      else                                                           setActiveSubTab('user');

    } else if (currentPath.includes(`/${URL_SEGMENTS.notify}`)) {
      setActiveTab('notify'); setActiveSubTab(null);

    } else if (
      currentPath.includes(`/${URL_SEGMENTS.admins}`) ||
      currentPath.includes(`/${URL_SEGMENTS.addAdmin}`) ||
      currentPath.includes(`/${URL_SEGMENTS.editAdmin}/`)
    ) {
      setActiveTab('admins'); setActiveSubTab(null);

    } else if (
      lastSegment === URL_SEGMENTS.resellers ||
      currentPath.includes(`/${URL_SEGMENTS.addReseller}`) ||
      currentPath.includes(`/${URL_SEGMENTS.editReseller}/`) ||
      currentPath.includes(`/${URL_SEGMENTS.resellers}/${URL_SEGMENTS.hierarchy}`)
    ) {
      setActiveTab('resellers'); setActiveSubTab(null);

    } else if (lastSegment === URL_SEGMENTS.account) {
      setActiveTab('account'); setActiveSubTab(null);

    } else {
      setActiveTab('resellers'); setActiveSubTab(null);
    }
  }, [location.pathname, location.search]);

  // ── Fetch data ────────────
  useEffect(() => {
    if (!['resellers', 'admins'].includes(activeTab)) return;
    if (activeTab === 'resellers' && !featureFlags.api.autoFetchResellers) return;
    if (activeTab === 'admins'    && !featureFlags.api.autoFetchAdmins)    return;

    const fetchData = async () => {
      try {
        const authToken = localStorage.getItem('token');
        if (!authToken) return;

        const apiEndpoint = activeTab === 'admins'
          ? `${process.env.REACT_APP_BACKEND_URL}/api/webmanager/admins`
          : `${process.env.REACT_APP_BACKEND_URL}/api/webmanager/resellers`;

        const apiResponse = await axios.get(apiEndpoint, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const responseData = Array.isArray(apiResponse.data.data) ? apiResponse.data.data : [];
        setUsers(responseData);
        setError(responseData.length === 0
          ? (activeTab === 'admins'
              ? SuperadminConfig.admins.emptyMessage
              : SuperadminConfig.resellers.emptyMessage)
          : '');
      } catch (fetchError) {
        console.error(`Failed to load ${activeTab}:`, fetchError?.response || fetchError);
        if (fetchError.response?.status === 401) { localStorage.clear(); navigate('/login'); }
        else setError(`Failed to load ${activeTab}.`);
      }
    };

    fetchData();
  }, [activeTab, navigate]);

  // ── Close dropdowns on outside click ──────
  useEffect(() => {
    const handleClickOutside = (clickEvent) => {
      if (clientsDropdownRef.current && !clientsDropdownRef.current.contains(clickEvent.target)) {
        setIsClientsDropdownOpen(false); setIsClientsPinned(false); setIsClientsHovered(false);
      }
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(clickEvent.target)) {
        setIsReportsDropdownOpen(false); setIsReportsPinned(false); setIsReportsHovered(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Tab → URL route map ───
  const tabToRouteSegment = {
    resellers : superadminRoutes.resellers,
    clients   : superadminRoutes.clients,
    reports   : superadminRoutes.reports,
    notify    : superadminRoutes.notify,
    admins    : superadminRoutes.admins,
    account   : superadminRoutes.account,
  };

  // ── Handlers 
  const closeAllDropdowns = () => {
    setIsClientsDropdownOpen(false); setIsReportsDropdownOpen(false);
    setIsClientsPinned(false);       setIsReportsPinned(false);
    setIsClientsHovered(false);      setIsReportsHovered(false);
  };

  const handleTabClick = (tabKey) => {
    if (tabKey === 'clients') {
      const shouldOpen = !isClientsPinned;
      setIsClientsPinned(shouldOpen); setIsClientsDropdownOpen(shouldOpen);
      setIsReportsPinned(false); setIsReportsDropdownOpen(false); setIsReportsHovered(false);
      return;
    }
    if (tabKey === 'reports') {
      const shouldOpen = !isReportsPinned;
      setIsReportsPinned(shouldOpen); setIsReportsDropdownOpen(shouldOpen);
      setIsClientsPinned(false); setIsClientsDropdownOpen(false); setIsClientsHovered(false);
      return;
    }
    setActiveTab(tabKey); setActiveSubTab(null); setError('');
    closeAllDropdowns();
    navigate(`/${URL_SEGMENTS.superadmin}/${tabToRouteSegment[tabKey] || tabKey}`);
  };

  const handleClientsSubTabClick = (subTabKey) => {
    setActiveTab('clients'); setActiveSubTab(subTabKey); setError('');
    const routeSegment = subTabKey === 'enterprise'
      ? superadminRoutes.enterpriseClients
      : superadminRoutes.clients;
    navigate(`/${URL_SEGMENTS.superadmin}/${routeSegment}`);
    setIsClientsDropdownOpen(false); setIsClientsPinned(false); setIsClientsHovered(false);
  };

  const handleReportsSubTabClick = (subTabKey) => {
    setActiveTab('reports'); setActiveSubTab(subTabKey); setError('');
    navigate(`/${URL_SEGMENTS.superadmin}/${superadminRoutes.reports}?sub=${subTabKey}`);
    setIsReportsDropdownOpen(false); setIsReportsPinned(false); setIsReportsHovered(false);
  };

  // ── Render content ────────
  const renderPageContent = () => {
    const currentPath = location.pathname;

    if (
      currentPath.includes(`/${URL_SEGMENTS.editClient}/`)           ||
      currentPath.includes(`/${URL_SEGMENTS.addClient}`)             ||
      currentPath.includes(`/${URL_SEGMENTS.editEnterpriseClient}/`) ||
      currentPath.includes(`/${URL_SEGMENTS.addEnterpriseClient}`)   ||
      currentPath.includes('/reseller/')                             ||
      currentPath.includes(`/${URL_SEGMENTS.resellers}/${URL_SEGMENTS.hierarchy}`) ||
      currentPath.includes(`/${URL_SEGMENTS.editAdmin}/`)            ||
      currentPath.includes(`/${URL_SEGMENTS.addAdmin}`)              ||
      currentPath.includes(`/${URL_SEGMENTS.notify}/${URL_SEGMENTS.emailLogs}`) ||
      currentPath.includes(`/${URL_SEGMENTS.addReseller}`)           ||
      currentPath.includes(`/${URL_SEGMENTS.editReseller}/`)
    ) {
      return <Outlet />;
    }

    switch (activeTab) {
      case 'resellers': return <Resellers users={users} setUsers={setUsers} />;
      case 'clients':   return activeSubTab === 'core' ? <Clients users={users} /> : <EnterpriseClients />;
      case 'admins':    return <Admins users={users} setUsers={setUsers} />;
      case 'account':   return <Account currentUser={currentUser} setError={setError} />;
      case 'notify':    return <Notify />;
      case 'reports': {
        const activeReportSubTab = activeSubTab || 'user';
        return <Reports activeSubTab={activeReportSubTab} />;
      }
      default: return null;
    }
  };

  // ── Style helpers ─────────
  const getDropdownItemStyle = (isActiveItem) => ({
    width: '100%', padding: '0.75rem 1rem', textAlign: 'left',
    background: isActiveItem ? theme.gradients.primary : 'transparent',
    color: isActiveItem ? '#fff' : theme.colors.textPrimary,
    border: 'none', cursor: 'pointer',
    fontSize: `${bodyFontSize}px`, fontFamily,
    transition: 'all 0.2s ease',
  });

  // ── Render ──
  return (
    <ErrorBoundary>
      <div style={{ fontFamily, fontSize: `${bodyFontSize}px`, width: '100%', boxSizing: 'border-box' }}>

        {/* ── Navigation tabs ── */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>

          {visibleTabs.map((tabKey) => {
            const tabNavConfig  = navConfig[tabKey];
            const isClientsTab  = tabKey === 'clients';
            const isReportsTab  = tabKey === 'reports';
            const hasDropdown   = isClientsTab || isReportsTab;

            return (
              <div
                key={tabKey}
                style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
                ref={isClientsTab ? clientsDropdownRef : isReportsTab ? reportsDropdownRef : null}
                onMouseEnter={() => {
                  if (isClientsTab) { setIsClientsHovered(true); setIsClientsDropdownOpen(true); }
                  if (isReportsTab) { setIsReportsHovered(true); setIsReportsDropdownOpen(true); }
                }}
                onMouseLeave={() => {
                  if (isClientsTab) { setIsClientsHovered(false); if (!isClientsPinned) setIsClientsDropdownOpen(false); }
                  if (isReportsTab) { setIsReportsHovered(false); if (!isReportsPinned) setIsReportsDropdownOpen(false); }
                }}
              >
                <button
                  style={{
                    width: '100%', padding: '0.5rem',
                    fontSize: `${bodyFontSize}px`, fontWeight: 'bold',
                    borderRadius: '6px', border: 'none',
                    background: activeTab === tabKey ? theme.gradients.primary : theme.gradients.secondary,
                    color: theme.colors.textWhite, cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '0.5rem', fontFamily,
                  }}
                  onClick={() => handleTabClick(tabKey)}
                >
                  <i
                    className={`bi bi-${tabNavConfig.icon}`}
                    style={{ fontSize: `${Math.max(bodyFontSize - 2, 10)}px` }}
                  />
                  <span>{tabNavConfig.label}</span>
                  {hasDropdown && <i className="bi bi-chevron-down" style={{ fontSize: '12px' }} />}
                </button>

                {/* Clients dropdown */}
                {isClientsTab && isClientsDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30, width: '100%', minWidth: '160px', overflow: 'hidden',
                  }}>
                    {featureFlags.clients.core && (
                      <button
                        style={getDropdownItemStyle(activeSubTab === 'core')}
                        onClick={() => handleClientsSubTabClick('core')}
                        onMouseOver={(e) => activeSubTab !== 'core'       && (e.currentTarget.style.background = theme.gradients.secondary)}
                        onMouseOut={(e)  => activeSubTab !== 'core'       && (e.currentTarget.style.background = 'transparent')}
                      >
                        {clientsConfig.core.label}
                      </button>
                    )}
                    {featureFlags.clients.core && featureFlags.clients.enterprise && (
                      <hr style={{ margin: 0, border: 'none', borderTop: `1px solid ${theme.colors.borderColor}`, opacity: 0.5 }} />
                    )}
                    {featureFlags.clients.enterprise && (
                      <button
                        style={getDropdownItemStyle(activeSubTab === 'enterprise')}
                        onClick={() => handleClientsSubTabClick('enterprise')}
                        onMouseOver={(e) => activeSubTab !== 'enterprise' && (e.currentTarget.style.background = theme.gradients.secondary)}
                        onMouseOut={(e)  => activeSubTab !== 'enterprise' && (e.currentTarget.style.background = 'transparent')}
                      >
                        {clientsConfig.enterprise.label}
                      </button>
                    )}
                  </div>
                )}

                {/* Reports dropdown */}
                {isReportsTab && isReportsDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0,
                    backgroundColor: theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                    zIndex: 30, width: '100%', minWidth: '180px', overflow: 'hidden',
                  }}>
                    {Object.entries(reportsConfig)
                      .filter(([reportKey]) => featureFlags.reports[reportKey])
                      .map(([reportKey, reportCfg], index, allReports) => (
                        <React.Fragment key={reportKey}>
                          <button
                            style={getDropdownItemStyle(activeSubTab === reportKey)}
                            onClick={() => handleReportsSubTabClick(reportKey)}
                            onMouseOver={(e) => activeSubTab !== reportKey && (e.currentTarget.style.background = theme.gradients.secondary)}
                            onMouseOut={(e)  => activeSubTab !== reportKey && (e.currentTarget.style.background = 'transparent')}
                          >
                            {reportCfg.label}
                          </button>
                          {index < allReports.length - 1 && (
                            <hr style={{ margin: 0, border: 'none', borderTop: `1px solid ${theme.colors.borderColor}`, opacity: 0.5 }} />
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                )}

              </div>
            );
          })}
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div style={{
            padding: '0.75rem 1.25rem', marginBottom: '1rem',
            border: `1px solid ${theme.colors.errorBorder}`, borderRadius: '0.25rem',
            color: theme.colors.errorText, backgroundColor: theme.colors.errorBg,
            textAlign: 'center', fontFamily, fontSize: `${bodyFontSize}px`,
          }}>{error}</div>
        )}

        {/* ── Page content ── */}
        <div style={{ width: '100%', boxSizing: 'border-box' }}>
          {renderPageContent()}
        </div>

      </div>
    </ErrorBoundary>
  );
};

export default SuperadminDashboard;